import { useTranslation } from 'react-i18next';

import type { MatchSummary } from '@/types/qriots';

interface MatchHistoryListProps {
  matches: MatchSummary[];
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  return `${m}m`;
}

function formatPlayedAt(iso: string): string {
  const diffMs = Date.now() - Date.parse(iso);
  const h = Math.floor(diffMs / 1000 / 3600);
  if (h < 1) return `${Math.max(1, Math.floor(diffMs / 60000))}m ago`;
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function MatchRow({ m }: { m: MatchSummary }) {
  const { t } = useTranslation('riot');
  const queue = m.queueLabel ?? `Queue ${m.queueId}`;
  return (
    <li
      className={[
        'flex items-center justify-between gap-3 rounded-lg px-3 py-2.5',
        m.win
          ? 'bg-success/10 border border-success/20'
          : 'bg-bg-tertiary border border-border',
      ].join(' ')}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex items-baseline gap-2 truncate">
          <span className="text-sm font-bold text-fg truncate">
            {m.championName ?? `Champion ${m.championId}`}
          </span>
          <span className="text-[10px] uppercase tracking-wider text-fg-muted truncate">
            {queue}
          </span>
        </div>
        <p className="text-xs text-fg-muted">
          {m.kda} · {m.teamPosition || '—'} · {formatDuration(m.gameDuration)}
        </p>
      </div>
      <div className="flex flex-col items-end gap-0.5">
        <span
          className={[
            'text-xs font-bold uppercase',
            m.win ? 'text-success' : 'text-fg-muted',
          ].join(' ')}
        >
          {m.win ? t('match.win') : t('match.loss')}
        </span>
        <span className="text-[10px] text-fg-muted">{formatPlayedAt(m.playedAt)}</span>
      </div>
    </li>
  );
}

export function MatchHistoryList({ matches }: MatchHistoryListProps) {
  const { t } = useTranslation('riot');

  if (matches.length === 0) {
    return (
      <p className="mx-4 rounded-lg bg-bg-secondary p-4 text-center text-sm text-fg-muted">
        {t('match.empty')}
      </p>
    );
  }

  return (
    <ul className="mx-4 flex flex-col gap-2">
      {matches.map((m) => (
        <MatchRow key={m.matchId} m={m} />
      ))}
    </ul>
  );
}
